import type { SortingState, ColumnVisibilityState as VisibilityState } from '@tanstack/react-table'
// bkz. components/data-table/DataTable.tsx: v9'da v8-uyumlu kolon yardımcısı `/legacy` altında.
import {
  legacyCreateColumnHelper as createColumnHelper,
  type LegacyColumnDef,
} from '@tanstack/react-table/legacy'

import {
  exportFieldValue,
  filterKind,
  formatFieldValue,
  isFormattedNumber,
  toNumber,
  type ReportField,
  type TableReport,
} from '@/lib/cube-report'
import { cn } from '@/lib/utils'
import type { ReportRow } from '@/services/cube/report'

const helper = createColumnHelper<ReportRow>()

/** Hücre içeriği; rapor tablosu ve pano tabloları ortak kullanır. */
export function renderCell(field: ReportField, value: unknown, row?: ReportRow) {
  if (field.image) {
    return typeof value === 'string' && value ? (
      <img src={value} alt="" className="h-10 w-16 object-contain" />
    ) : null
  }
  const text = formatFieldValue(field, value, row)
  if (!isFormattedNumber(field)) return text
  const number = field.signColor ? toNumber(value) : null
  return (
    <span
      className={cn(
        'tabular block text-right',
        number !== null && number < 0 && 'text-destructive',
        number !== null && number > 0 && 'text-positive',
      )}
    >
      {text}
    </span>
  )
}

/**
 * Kolonlar view'ın `includes` sırasıyla, satır kimliği (`rowKey`) hariç. Etiket ve
 * biçim Cube meta'sından gelir; burada iş alanı bilgisi yok.
 */
export function buildReportColumns(
  report: {
    dimensions: ReportField[]
    /** Satır kimliği kolonu (gösterilmez); keşfet satırlarında olmayabilir. */
    rowKey?: string
    sortable: ReadonlySet<string>
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): LegacyColumnDef<ReportRow, any>[] {
  return report.dimensions
    .filter((field) => field.name !== report.rowKey)
    .map((field) =>
      // `accessor(field.name, ...)` KULLANILMAZ: TanStack nokta içeren string anahtarı
      // derin yol sayar (`row.Tartim.tarih`), Cube satırı ise düz (`row['Tartim.tarih']`)
      // — her hücre "-" olurdu. Ayrıca türetilen kimlik `Tartim_tarih` olur, görünürlük
      // ve sıralama anahtarlarıyla eşleşmezdi. Erişim fonksiyonu + açık `id` ikisini de çözer.
      helper.accessor((row) => row[field.name], {
        id: field.name,
        header: field.label,
        enableSorting: report.sortable.has(field.name),
        // `cell` her zaman verilir: `undefined` TanStack'in varsayılan hücresini ezip
        // hücreyi boş bırakıyordu (bkz. git log aeeb74b).
        cell: (info) => renderCell(field, info.getValue(), info.row.original),
        meta: {
          title: field.label,
          exportValue: (row: ReportRow) => exportFieldValue(field, row[field.name]),
          exportNumberFormat: field.numberFormat,
          // Resim kolonunun değeri bir dosya yolu: filtrelenmez, ipucu olarak gösterilmez.
          filterKind: field.image ? undefined : filterKind(field),
          ...(field.image && { cellTooltip: false as const }),
          numeric: isFormattedNumber(field),
        },
      }),
    )
}

export function defaultColumnVisibility(report: TableReport): VisibilityState {
  return Object.fromEntries([...report.hidden].map((member) => [member, false]))
}

/** `sort` parametresi: `-Tartim.tarih,Tartim.cariAdi` (eksi = azalan). */
export function parseSort(
  raw: string | undefined,
  report: { sortable: ReadonlySet<string> },
): SortingState {
  if (!raw) return []
  return raw
    .split(',')
    .map((token) => ({ id: token.replace(/^-/, ''), desc: token.startsWith('-') }))
    .filter((item) => report.sortable.has(item.id))
}
